// Notification data access with caching and single-flight
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, tap, finalize, shareReplay } from 'rxjs/operators';
import {
  NotificationProxy,
  MyNotificationsViewModel,
  MyNotificationsViewModelListResponseResult,
  BooleanResponseResult,
} from '@/app/proxy/proxies';
import { AuthService } from '@/app/auth/shared/services/auth.service';
import { DateTime } from 'luxon';

export interface NotificationCache {
  notifications: MyNotificationsViewModel[];
  lastFetchTime: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private readonly CACHE_KEY = 'notificationCache';
  private readonly CACHE_EXPIRY = 30 * 1000;
  private readonly LAST_NOTIFICATION_TIMESTAMP_KEY = 'lastNotificationTimestamp';
  private readonly SESSION_CACHE_KEY = 'notificationSessionCache';

  private cache: NotificationCache = {
    notifications: [],
    lastFetchTime: 0,
  };

  private inFlightFetch$: Observable<MyNotificationsViewModel[]> | null = null;
  private inFlightUnreadFetch$: Observable<MyNotificationsViewModel[]> | null = null;

  private lastNotificationTimestamp: string | null = null;

  private notificationsSubject = new BehaviorSubject<MyNotificationsViewModel[]>([]);
  private newNotificationsSubject = new BehaviorSubject<MyNotificationsViewModel[]>([]);
  private realtimeNotificationsSubject = new Subject<MyNotificationsViewModel>();
  private unreadCountSubject = new BehaviorSubject<number>(0);

  public notifications$ = this.notificationsSubject.asObservable();
  public newNotifications$ = this.newNotificationsSubject.asObservable();
  public realtimeNotifications$ = this.realtimeNotificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(
    private notificationProxy: NotificationProxy,
    private authService: AuthService
  ) {
    this.loadSessionCache();
    this.loadLastNotificationTimestamp();

    this.authService.userSession$.subscribe((isAuthenticated) => {
      if (!isAuthenticated) {
        this.clearSessionCache();
      }
    });
  }

  ngOnDestroy(): void {
    this.saveSessionCache();
    this.saveLastNotificationTimestamp();
  }

  public fetchNotifications(forceRefresh: boolean = false): Observable<MyNotificationsViewModel[]> {
    if (!forceRefresh && this.isCacheValid()) {
      this.updateSubjects();
      return of(this.cache.notifications);
    }

    if (!this.authService.isAuthenticated()) {
      return of([]);
    }

    if (this.inFlightFetch$) {
      return this.inFlightFetch$;
    }

    const request$ = this.notificationProxy.notification_GetMyNotifications().pipe(
      catchError((_error) => {
        if (this.cache.notifications.length > 0) {
          return of({ result: this.cache.notifications } as MyNotificationsViewModelListResponseResult);
        }
        return of({ result: [] } as MyNotificationsViewModelListResponseResult);
      }),
      map((response) => response.result || []),
      tap((notifications) => {
        this.processNewNotifications(notifications);
        this.updateSessionCache(notifications);
        this.updateSubjects();
      }),
      finalize(() => {
        this.inFlightFetch$ = null;
      })
    );

    this.inFlightFetch$ = request$.pipe(shareReplay(1));
    return this.inFlightFetch$;
  }

  public fetchUnreadNotifications(): Observable<MyNotificationsViewModel[]> {
    if (this.inFlightUnreadFetch$) {
      return this.inFlightUnreadFetch$;
    }

    const request$ = this.notificationProxy
      .notification_GetMyNotifications()
      .pipe(
        map((response) => response.result || []),
        map((notifications) => notifications.filter((n) => !n.isSeen)),
        tap((unreadNotifications) => {
          this.unreadCountSubject.next(unreadNotifications.length);
        }),
        finalize(() => {
          this.inFlightUnreadFetch$ = null;
        })
      );

    this.inFlightUnreadFetch$ = request$.pipe(shareReplay(1));
    return this.inFlightUnreadFetch$;
  }

  public getCachedNotifications(): MyNotificationsViewModel[] {
    return [...this.cache.notifications];
  }

  public getNewNotifications(): MyNotificationsViewModel[] {
    return this.newNotificationsSubject.value;
  }

  public getUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  public markAsSeen(notification: MyNotificationsViewModel): Observable<boolean> {
    this.notificationProxy
      .notification_MarkSeen(notification.id)
      .pipe(
        catchError((_error) => {
          return of(false);
        })
      )
      .subscribe((_result) => {
        if (!notification.isSeen) {
          notification.isSeen = true;
          this.updateSubjects();
          this.saveSessionCache();
        }
      });

    return of(true);
  }

  public markAllAsSeen(): Observable<boolean> {
    this.notificationProxy
      .notification_MarkAllSeen()
      .pipe(
        catchError((_error) => {
          return of(false);
        })
      )
      .subscribe((result) => {
        if ((result as BooleanResponseResult).isSuccess) {
          this.cache.notifications.forEach((notification) => {
            notification.isSeen = true;
          });
          this.updateSubjects();
          this.saveSessionCache();
        }
      });

    return of(true);
  }

  public refreshNotifications(): Observable<MyNotificationsViewModel[]> {
    return this.fetchNotifications(true);
  }

  public fetchOnPanelOpen(): Observable<MyNotificationsViewModel[]> {
    return this.fetchNotifications(true);
  }

  public clearSessionCache(): void {
    this.cache = {
      notifications: [],
      lastFetchTime: 0,
    };
    this.notificationsSubject.next([]);
    this.newNotificationsSubject.next([]);
    this.unreadCountSubject.next(0);
    this.saveSessionCache();
    this.clearLastNotificationTimestamp();
  }

  public forceEmitNewNotifications(notifications: MyNotificationsViewModel[]): void {
    this.newNotificationsSubject.next(notifications);
  }

  public getLastNotificationTimestamp(): string | null {
    return this.lastNotificationTimestamp;
  }

  public setLastNotificationTimestamp(timestamp: string | null): void {
    this.lastNotificationTimestamp = timestamp;
    this.saveLastNotificationTimestamp();
  }

  /**
   * Add a real-time notification from SignalR (prepend + dedupe + emit)
   */
  public addRealtimeNotification(notification: MyNotificationsViewModel): void {
    // Dedupe by id - if it already exists, don't add it again
    const exists = this.cache.notifications.some((n) => n.id === notification.id);
    if (!exists) {
      this.realtimeNotificationsSubject.next(notification);

      // Prepend the new notification to cache
      this.cache.notifications = [notification, ...this.cache.notifications];
      this.cache.lastFetchTime = Date.now();
      
      // Update session cache
      this.saveSessionCache();
      
      // Update all subjects
      this.updateSubjects();
      
      // Emit as new notification if unseen
      if (!notification.isSeen) {
        const currentNew = this.newNotificationsSubject.value;
        const alreadyInNew = currentNew.some((n) => n.id === notification.id);
        if (!alreadyInNew) {
          this.newNotificationsSubject.next([notification, ...currentNew]);
        }
      }
      
      // Update timestamp tracking
      const notificationTimestamp = notification.createdAt.toISO();
      if (!this.lastNotificationTimestamp || notificationTimestamp > this.lastNotificationTimestamp) {
        this.lastNotificationTimestamp = notificationTimestamp;
        this.saveLastNotificationTimestamp();
      }
    }
  }

  private isCacheValid(): boolean {
    const now = Date.now();
    return now - this.cache.lastFetchTime < this.CACHE_EXPIRY;
  }

  private updateSessionCache(notifications: MyNotificationsViewModel[]): void {
    this.cache.notifications = notifications;
    this.cache.lastFetchTime = Date.now();
    this.saveSessionCache();
  }

  private updateSubjects(): void {
    this.notificationsSubject.next([...this.cache.notifications]);

    const unreadCount = this.cache.notifications.filter((n) => !n.isSeen).length;
    this.unreadCountSubject.next(unreadCount);
  }

  private processNewNotifications(notifications: MyNotificationsViewModel[]): void {
    if (!notifications || notifications.length === 0) {
      return;
    }

    const newNotifications = this.findNewNotificationsByTimestamp(notifications);

    const newUnseenNotifications = newNotifications.filter((notification) => !notification.isSeen);

    if (newUnseenNotifications.length > 0) {
      this.newNotificationsSubject.next(newUnseenNotifications);
    }

    this.updateLastNotificationTimestamp(notifications);
  }

  private findNewNotificationsByTimestamp(notifications: MyNotificationsViewModel[]): MyNotificationsViewModel[] {
    if (!this.lastNotificationTimestamp) {
      return [];
    }

    const lastTimestamp = DateTime.fromISO(this.lastNotificationTimestamp);

    return notifications.filter((notification) => {
      return notification.createdAt > lastTimestamp;
    });
  }

  private updateLastNotificationTimestamp(notifications: MyNotificationsViewModel[]): void {
    if (notifications.length === 0) {
      return;
    }

    const mostRecentNotification = notifications.reduce((latest, current) => {
      return current.createdAt > latest.createdAt ? current : latest;
    });

    const mostRecentTimestamp = mostRecentNotification.createdAt.toISO();

    if (!this.lastNotificationTimestamp || mostRecentTimestamp > this.lastNotificationTimestamp) {
      this.lastNotificationTimestamp = mostRecentTimestamp;
      this.saveLastNotificationTimestamp();
    }
  }

  private loadSessionCache(): void {
    try {
      const stored = sessionStorage.getItem(this.SESSION_CACHE_KEY);
      if (stored) {
        const parsedCache = JSON.parse(stored);
        this.cache = {
          notifications: parsedCache.notifications || [],
          lastFetchTime: parsedCache.lastFetchTime || 0,
        };
        this.updateSubjects();
      }
    } catch (_error) {
      this.cache = {
        notifications: [],
        lastFetchTime: 0,
      };
    }
  }

  private saveSessionCache(): void {
    try {
      const cacheToSave = {
        notifications: this.cache.notifications,
        lastFetchTime: this.cache.lastFetchTime,
      };
      sessionStorage.setItem(this.SESSION_CACHE_KEY, JSON.stringify(cacheToSave));
    } catch (_error) { return; }
  }

  private loadLastNotificationTimestamp(): void {
    try {
      const stored = localStorage.getItem(this.LAST_NOTIFICATION_TIMESTAMP_KEY);
      this.lastNotificationTimestamp = stored;
    } catch (_error) {
      this.lastNotificationTimestamp = null;
    }
  }

  private saveLastNotificationTimestamp(): void {
    try {
      if (this.lastNotificationTimestamp) {
        localStorage.setItem(this.LAST_NOTIFICATION_TIMESTAMP_KEY, this.lastNotificationTimestamp);
      }
    } catch (_error) { return; }
  }

  private clearLastNotificationTimestamp(): void {
    this.lastNotificationTimestamp = null;
    localStorage.removeItem(this.LAST_NOTIFICATION_TIMESTAMP_KEY);
  }
}
